/* global APP_VERSION -- injected by Vite define plugin at build time */
import { datadogRum } from '@datadog/browser-rum';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { useEffect } from 'react';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { Userpilot } from 'userpilot';

import { AppLayout } from '../components/layout';
import { getAgentDetailsFromEtsToken } from '../../resources/login/operations';
import { Restricted } from '../components/Restricted';

import { AmplitudeProvider } from '../analytics';
import { sagaMiddleware } from '../redux/middlewares/saga';
import { rootSaga } from '../redux/rootSaga';
import { store } from '../redux/store';
import { getEnv, getEnvRequired } from '../utilities/env';

import { getCookieFromName } from '../utilities/cookies';

import { AuthProvider } from './AuthProvider';
import { queryClient } from './react-query-provider';
import { WebSocketProvider } from './WebSocketProvider';
import { LocalizationProvider } from './LocalizationProvider';

const userPilotToken = getEnv('USER_PILOT_TOKEN');

const env = getEnv('APP_ENV');

if (env === 'staging' || env === 'production') {
    const ignoredErrors = ['Failed to fetch'];
    datadogRum.init({
        applicationId: '3353a3a9-b6c8-4916-bac9-e81023a1658c', // gitleaks:allow
        clientToken: 'pub12ba172bd552462e0a576833405cde4e', // gitleaks:allow
        // `site` refers to the Datadog site parameter of your organization
        // see https://docs.datadoghq.com/getting_started/site/
        site: 'datadoghq.com',
        service: 'request-manager-app',
        env: env,
        // @ts-expect-error APP_VERSION is injected by Vite at build time (define in vite.config.ts)
        version: APP_VERSION,
        sessionSampleRate: 10,
        sessionReplaySampleRate: 0,
        trackUserInteractions: true,
        trackResources: true,
        trackLongTasks: true,
        defaultPrivacyLevel: 'mask-user-input',
        beforeSend: (event) =>
            !(
                event.type === 'error' &&
                ignoredErrors.some((ignoredError) => event.error?.message?.includes(ignoredError))
            ),
    });
}

if (userPilotToken) Userpilot.initialize(userPilotToken);

sagaMiddleware.run(rootSaga);

export const AppProvider = () => {
    const isImpersonated = getCookieFromName('impersonate') === 'true';
    const agentDetails = getAgentDetailsFromEtsToken();

    useEffect(() => {
        if (agentDetails?.userId) {
            datadogRum.setUser({
                id: agentDetails.userId.toString(),
                agencyId: agentDetails.agencyId,
                isImpersonated: isImpersonated,
            });
        }
    }, [agentDetails, isImpersonated]);

    return (
        <Provider store={store}>
            <LocalizationProvider>
                <AuthProvider>
                    <WebSocketProvider>
                        <QueryClientProvider client={queryClient}>
                            <AmplitudeProvider>
                                <BrowserRouter
                                    basename={getEnvRequired('APP_BASENAME')}
                                    future={{
                                        v7_startTransition: true,
                                        v7_relativeSplatPath: true,
                                    }}
                                >
                                    <Restricted>
                                        <AppLayout />
                                    </Restricted>
                                </BrowserRouter>
                            </AmplitudeProvider>
                            <ReactQueryDevtools initialIsOpen={false} />
                        </QueryClientProvider>
                    </WebSocketProvider>
                </AuthProvider>
            </LocalizationProvider>
        </Provider>
    );
};
